import { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
interface LayoutProps {
  children: React.ReactNode;
}
export default function Layout({
  children
}: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };
    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, []);
  const navLinks = [{
    to: ROUTE_PATHS.HOME,
    label: 'Home'
  }, {
    to: ROUTE_PATHS.ABOUT,
    label: 'About'
  }, {
    to: ROUTE_PATHS.FILMOGRAPHY,
    label: 'Filmography'
  }, {
    to: ROUTE_PATHS.BEHIND_THE_SCENES,
    label: 'Behind the Scenes'
  }, {
    to: ROUTE_PATHS.PRESS,
    label: 'Press'
  }];
  return <div className="min-h-screen bg-background text-foreground">
      <header ref={headerRef} className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <NavLink to={ROUTE_PATHS.HOME} className="flex flex-col">
              <span className="font-display text-2xl md:text-3xl font-bold text-accent tracking-wider">
                KESHHAV PANNERIY
              </span>
              <span className="text-xs md:text-sm text-muted-foreground tracking-[0.3em] ml-1">FILMMAKER</span>
            </NavLink>

            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map(link => <NavLink key={link.to} to={link.to} className={({
              isActive
            }) => `text-sm font-medium tracking-wide transition-colors hover:text-accent ${isActive ? 'text-accent' : 'text-foreground'}`}>
                  {link.label}
                </NavLink>)}
            </nav>

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-foreground hover:text-accent transition-colors" aria-label="Toggle menu">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && <div className="lg:hidden border-t border-border bg-background">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-4">
              {navLinks.map(link => <NavLink key={link.to} to={link.to} onClick={() => setMobileMenuOpen(false)} className={({
            isActive
          }) => `text-base font-medium tracking-wide transition-colors hover:text-accent ${isActive ? 'text-accent' : 'text-foreground'}`}>
                  {link.label}
                </NavLink>)}
            </nav>
          </div>}
      </header>

      <main style={{
      paddingTop: `${headerHeight}px`
    }}>{children}</main>

      <footer className="bg-card border-t border-border mt-20">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-display text-xl font-bold text-accent mb-4">
                BIBIA FILMS STUDIOS
              </h3>
              <p className="text-sm text-muted-foreground">
                Crafting cinematic excellence since 2016
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground mb-4">Contact</h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Mumbai · Hollywood · Global</p>
                <p>
                  <a href="mailto:info@keshhavpanneriy.com" className="hover:text-accent transition-colors">
                    info@keshhavpanneriy.com
                  </a>
                </p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-foreground mb-4">Follow</h4>
              <div className="flex gap-4">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent transition-colors" aria-label="Facebook">
                  <Facebook size={20} />
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent transition-colors" aria-label="Instagram">
                  <Instagram size={20} />
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent transition-colors" aria-label="LinkedIn">
                  <Linkedin size={20} />
                </a>
                <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent transition-colors" aria-label="X">
                  <Twitter size={20} />
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-border text-center text-sm text-muted-foreground">
            <p>© 2026 Keshhav Panneriy. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>;
}